import React, { useState } from 'react';
import { CheckCircle, Search, Phone, MessageSquare, Mail, Linkedin, Building2, User, MapPin, Target, Briefcase, Globe, Zap } from 'lucide-react';

interface CustomizationSectionProps {
  onOpenDemoModal: () => void;
}

const CustomizationSection: React.FC<CustomizationSectionProps> = ({ onOpenDemoModal }) => {
  const [activeChipCategory, setActiveChipCategory] = useState<string>('industry');
  const [activePreviewOption, setActivePreviewOption] = useState<string>('saas');

  const chips = [
    { id: 'industry', label: 'Industry / ICP' },
    { id: 'tone', label: 'Tone' },
    { id: 'channels', label: 'Channels' },
    { id: 'cadence', label: 'Cadence' }
  ];

  const previewData = {
    industry: {
      options: [
        { id: 'saas', label: 'SaaS' },
        { id: 'ecommerce', label: 'E-commerce' },
        { id: 'fintech', label: 'FinTech' },
        { id: 'healthcare', label: 'Healthcare' }
      ],
      content: {
        saas: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Search className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Lead Gen - SaaS Focus</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-bg-surface rounded-lg border border-card-border">
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-3 h-3 text-brand-blue" />
                    <div>
                      <div className="text-text-primary text-xs font-medium">CloudScale Solutions</div>
                      <div className="text-text-secondary text-xs">SaaS • 150+ employees</div>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-brand-green-teal rounded-full" />
                </div>
                <div className="flex items-center justify-between p-2 bg-bg-surface rounded-lg border border-card-border">
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-3 h-3 text-brand-blue" />
                    <div>
                      <div className="text-text-primary text-xs font-medium">DataFlow Analytics</div>
                      <div className="text-text-secondary text-xs">SaaS • 200+ employees</div>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-brand-green-teal rounded-full" />
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Phone className="w-4 h-4 text-brand-green-teal" />
                <span className="text-text-primary font-semibold text-sm">Call Script</span>
              </div>
              <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
                <div className="text-text-primary text-sm">
                  "Hi Sarah, I noticed CloudScale just hit 150+ employees. With that growth, I imagine your team is feeling the pressure to scale their customer acquisition..."
                </div>
              </div>
            </div>
          </div>
        ),
        ecommerce: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Search className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Lead Gen - E-commerce Focus</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-bg-surface rounded-lg border border-card-border">
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-3 h-3 text-brand-blue" />
                    <div>
                      <div className="text-text-primary text-xs font-medium">ShopTech Direct</div>
                      <div className="text-text-secondary text-xs">E-commerce • $10M+ revenue</div>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-brand-green-teal rounded-full" />
                </div>
                <div className="flex items-center justify-between p-2 bg-bg-surface rounded-lg border border-card-border">
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-3 h-3 text-brand-blue" />
                    <div>
                      <div className="text-text-primary text-xs font-medium">RetailFlow Systems</div>
                      <div className="text-text-secondary text-xs">E-commerce • $25M+ revenue</div>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-brand-green-teal rounded-full" />
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Mail className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Email Subject</span>
              </div>
              <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
                <div className="text-text-primary text-sm font-medium mb-1">
                  Quick question about ShopTech's Q4 growth strategy
                </div>
                <div className="text-text-secondary text-sm">
                  Hi Mike, saw ShopTech hit $10M ARR—congrats! With Black Friday approaching, how are you handling the increased customer acquisition demands?
                </div>
              </div>
            </div>
          </div>
        ),
        fintech: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Search className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Lead Gen - FinTech Focus</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-bg-surface rounded-lg border border-card-border">
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-3 h-3 text-brand-blue" />
                    <div>
                      <div className="text-text-primary text-xs font-medium">PayStream Solutions</div>
                      <div className="text-text-secondary text-xs">FinTech • Series B</div>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-brand-green-teal rounded-full" />
                </div>
                <div className="flex items-center justify-between p-2 bg-bg-surface rounded-lg border border-card-border">
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-3 h-3 text-brand-blue" />
                    <div>
                      <div className="text-text-primary text-xs font-medium">CryptoFlow Analytics</div>
                      <div className="text-text-secondary text-xs">FinTech • Series A</div>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-brand-green-teal rounded-full" />
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Linkedin className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">LinkedIn Message</span>
              </div>
              <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
                <div className="text-text-primary text-sm">
                  "Hi Jennifer, congrats on PayStream's Series B! With the new funding, I imagine you're scaling fast. How are you handling compliance while growing your sales team?"
                </div>
              </div>
            </div>
          </div>
        ),
        healthcare: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Search className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Lead Gen - Healthcare Focus</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-bg-surface rounded-lg border border-card-border">
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-3 h-3 text-brand-blue" />
                    <div>
                      <div className="text-text-primary text-xs font-medium">MedTech Innovations</div>
                      <div className="text-text-secondary text-xs">Healthcare • 500+ beds</div>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-brand-green-teal rounded-full" />
                </div>
                <div className="flex items-center justify-between p-2 bg-bg-surface rounded-lg border border-card-border">
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-3 h-3 text-brand-blue" />
                    <div>
                      <div className="text-text-primary text-xs font-medium">HealthFlow Systems</div>
                      <div className="text-text-secondary text-xs">Healthcare • Multi-location</div>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-brand-green-teal rounded-full" />
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Phone className="w-4 h-4 text-brand-green-teal" />
                <span className="text-text-primary font-semibold text-sm">Call Script</span>
              </div>
              <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
                <div className="text-text-primary text-sm">
                  "Hi Dr. Smith, I know patient care is your top priority. I wanted to share how MedTech reduced their administrative overhead by 40% while improving patient outcomes..."
                </div>
              </div>
            </div>
          </div>
        )
      }
    },
    tone: {
      options: [
        { id: 'professional', label: 'Professional' },
        { id: 'casual', label: 'Casual' },
        { id: 'consultative', label: 'Consultative' },
        { id: 'direct', label: 'Direct' }
      ],
      content: {
        professional: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Mail className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Professional Tone</span>
              </div>
              <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
                <div className="text-text-primary text-sm font-medium mb-1">
                  Partnership opportunity regarding sales optimization
                </div>
                <div className="text-text-secondary text-sm">
                  Dear Mr. Johnson, I hope this message finds you well. I am reaching out to discuss how our solutions have assisted organizations similar to yours in achieving measurable improvements in their sales processes...
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Phone className="w-4 h-4 text-brand-green-teal" />
                <span className="text-text-primary font-semibold text-sm">Professional Call</span>
              </div>
              <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
                <div className="text-text-primary text-sm">
                  "Good morning, Mr. Johnson. I'm calling from SalesPad regarding the sales optimization solutions we discussed. Would you have a few minutes to explore how we might support your organization's growth objectives?"
                </div>
              </div>
            </div>
          </div>
        ),
        casual: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <MessageSquare className="w-4 h-4 text-brand-green-teal" />
                <span className="text-text-primary font-semibold text-sm">Casual Tone</span>
              </div>
              <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
                <div className="text-text-primary text-sm">
                  "Hey Mike! 👋 Saw your post about scaling challenges. We've helped tons of companies like yours automate their sales process. Want to chat about it over coffee? ☕"
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Linkedin className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Casual LinkedIn</span>
              </div>
              <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
                <div className="text-text-primary text-sm">
                  "Hi Sarah! Love what you're building at TechFlow. We're helping similar companies automate their outreach - would love to share some insights if you're interested! 🚀"
                </div>
              </div>
            </div>
          </div>
        ),
        consultative: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Phone className="w-4 h-4 text-brand-green-teal" />
                <span className="text-text-primary font-semibold text-sm">Consultative Approach</span>
              </div>
              <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
                <div className="text-text-primary text-sm">
                  "Hi Jennifer, I've been researching companies in your space and noticed some interesting trends. What challenges are you seeing with lead generation as you scale to 200+ employees?"
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Mail className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Consultative Email</span>
              </div>
              <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
                <div className="text-text-primary text-sm font-medium mb-1">
                  Insights on scaling sales operations
                </div>
                <div className="text-text-secondary text-sm">
                  Hi David, I've been analyzing growth patterns in the SaaS space and wanted to share some insights that might be relevant to CloudScale's expansion. What's your biggest bottleneck right now?
                </div>
              </div>
            </div>
          </div>
        ),
        direct: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <MessageSquare className="w-4 h-4 text-brand-green-teal" />
                <span className="text-text-primary font-semibold text-sm">Direct Approach</span>
              </div>
              <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
                <div className="text-text-primary text-sm">
                  "Alex, I can help you generate 50+ qualified leads per month. 15-minute demo this week?"
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Mail className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Direct Email</span>
              </div>
              <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
                <div className="text-text-primary text-sm font-medium mb-1">
                  Cut your sales time in half
                </div>
                <div className="text-text-secondary text-sm">
                  Hi Lisa, we automate sales outreach for companies like yours. Result: 3x more meetings, 50% less manual work. Demo available this week.
                </div>
              </div>
            </div>
          </div>
        )
      }
    },
    channels: {
      options: [
        { id: 'multichannel', label: 'Multi-Channel' },
        { id: 'email-focused', label: 'Email Focused' },
        { id: 'social-first', label: 'Social First' },
        { id: 'phone-heavy', label: 'Phone Heavy' }
      ],
      content: {
        multichannel: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Globe className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Multi-Channel Sequence</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Mail className="w-3 h-3 text-brand-blue" />
                  <div className="text-text-primary text-xs">Day 1: Email introduction</div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Linkedin className="w-3 h-3 text-brand-blue" />
                  <div className="text-text-primary text-xs">Day 3: LinkedIn connection</div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Phone className="w-3 h-3 text-brand-green-teal" />
                  <div className="text-text-primary text-xs">Day 5: Follow-up call</div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <MessageSquare className="w-3 h-3 text-brand-green-teal" />
                  <div className="text-text-primary text-xs">Day 7: WhatsApp message</div>
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-4 h-4 text-brand-green-teal" />
                <span className="text-text-primary font-semibold text-sm">Coverage</span>
              </div>
              <div className="text-text-primary text-sm">
                Maximum touchpoints across all channels for highest engagement rates
              </div>
            </div>
          </div>
        ),
        'email-focused': (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Mail className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Email-Focused Campaign</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Mail className="w-3 h-3 text-brand-blue" />
                  <div className="text-text-primary text-xs">Day 1: Introduction email</div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Mail className="w-3 h-3 text-brand-blue" />
                  <div className="text-text-primary text-xs">Day 4: Value-add follow-up</div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Mail className="w-3 h-3 text-brand-blue" />
                  <div className="text-text-primary text-xs">Day 8: Case study share</div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Mail className="w-3 h-3 text-brand-blue" />
                  <div className="text-text-primary text-xs">Day 12: Final outreach</div>
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-4 h-4 text-brand-green-teal" />
                <span className="text-text-primary font-semibold text-sm">Benefits</span>
              </div>
              <div className="text-text-primary text-sm">
                High volume, cost-effective, detailed tracking and personalization
              </div>
            </div>
          </div>
        ),
        'social-first': (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Linkedin className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Social-First Strategy</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Linkedin className="w-3 h-3 text-brand-blue" />
                  <div className="text-text-primary text-xs">Day 1: LinkedIn profile view</div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Linkedin className="w-3 h-3 text-brand-blue" />
                  <div className="text-text-primary text-xs">Day 2: Connection request</div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <MessageSquare className="w-3 h-3 text-brand-green-teal" />
                  <div className="text-text-primary text-xs">Day 5: LinkedIn message</div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Mail className="w-3 h-3 text-brand-blue" />
                  <div className="text-text-primary text-xs">Day 8: Email follow-up</div>
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-4 h-4 text-brand-green-teal" />
                <span className="text-text-primary font-semibold text-sm">Advantage</span>
              </div>
              <div className="text-text-primary text-sm">
                Build relationships first, higher trust and engagement rates
              </div>
            </div>
          </div>
        ),
        'phone-heavy': (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Phone className="w-4 h-4 text-brand-green-teal" />
                <span className="text-text-primary font-semibold text-sm">Phone-Heavy Approach</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Mail className="w-3 h-3 text-brand-blue" />
                  <div className="text-text-primary text-xs">Day 1: Pre-call email</div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Phone className="w-3 h-3 text-brand-green-teal" />
                  <div className="text-text-primary text-xs">Day 2: First call attempt</div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Phone className="w-3 h-3 text-brand-green-teal" />
                  <div className="text-text-primary text-xs">Day 4: Second call attempt</div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
                  <Phone className="w-3 h-3 text-brand-green-teal" />
                  <div className="text-text-primary text-xs">Day 7: Final call attempt</div>
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-4 h-4 text-brand-green-teal" />
                <span className="text-text-primary font-semibold text-sm">Impact</span>
              </div>
              <div className="text-text-primary text-sm">
                Direct conversation, immediate feedback, faster deal progression
              </div>
            </div>
          </div>
        )
      }
    },
    cadence: {
      options: [
        { id: 'aggressive', label: 'Aggressive' },
        { id: 'standard', label: 'Standard' },
        { id: 'gentle', label: 'Gentle' },
        { id: 'nurture', label: 'Long Nurture' }
      ],
      content: {
        aggressive: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Aggressive Cadence - 7 Days</span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 1: Email + LinkedIn</div>
                  <div className="text-brand-blue text-xs">2 touches</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 2: Call + WhatsApp</div>
                  <div className="text-brand-green-teal text-xs">2 touches</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 4: Email follow-up</div>
                  <div className="text-brand-blue text-xs">1 touch</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 6: Call + LinkedIn</div>
                  <div className="text-brand-green-teal text-xs">2 touches</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 7: Final email</div>
                  <div className="text-brand-blue text-xs">1 touch</div>
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="text-text-primary text-sm font-medium mb-1">Total: 8 touchpoints in 7 days</div>
              <div className="text-text-secondary text-xs">Best for: Hot leads, time-sensitive opportunities</div>
            </div>
          </div>
        ),
        standard: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Standard Cadence - 14 Days</span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 1: Email introduction</div>
                  <div className="text-brand-blue text-xs">1 touch</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 3: LinkedIn connection</div>
                  <div className="text-brand-blue text-xs">1 touch</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 6: Call attempt</div>
                  <div className="text-brand-green-teal text-xs">1 touch</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 9: Email follow-up</div>
                  <div className="text-brand-blue text-xs">1 touch</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 14: Final outreach</div>
                  <div className="text-brand-green-teal text-xs">1 touch</div>
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="text-text-primary text-sm font-medium mb-1">Total: 5 touchpoints in 14 days</div>
              <div className="text-text-secondary text-xs">Best for: Most prospects, balanced approach</div>
            </div>
          </div>
        ),
        gentle: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Gentle Cadence - 21 Days</span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 1: Email introduction</div>
                  <div className="text-brand-blue text-xs">1 touch</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 7: LinkedIn connection</div>
                  <div className="text-brand-blue text-xs">1 touch</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 14: Value-add email</div>
                  <div className="text-brand-blue text-xs">1 touch</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Day 21: Soft follow-up</div>
                  <div className="text-brand-green-teal text-xs">1 touch</div>
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="text-text-primary text-sm font-medium mb-1">Total: 4 touchpoints in 21 days</div>
              <div className="text-text-secondary text-xs">Best for: Enterprise, relationship-focused sales</div>
            </div>
          </div>
        ),
        nurture: (
          <div className="space-y-4">
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-4 h-4 text-brand-blue" />
                <span className="text-text-primary font-semibold text-sm">Long Nurture - 90 Days</span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Week 1: Introduction</div>
                  <div className="text-brand-blue text-xs">1 touch</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Week 4: Industry insights</div>
                  <div className="text-brand-blue text-xs">1 touch</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Week 8: Case study share</div>
                  <div className="text-brand-blue text-xs">1 touch</div>
                </div>
                <div className="flex items-center justify-between p-1 bg-bg-surface rounded border border-card-border">
                  <div className="text-text-primary text-xs">Week 12: Soft check-in</div>
                  <div className="text-brand-green-teal text-xs">1 touch</div>
                </div>
              </div>
            </div>
            <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
              <div className="text-text-primary text-sm font-medium mb-1">Total: 4 touchpoints in 90 days</div>
              <div className="text-text-secondary text-xs">Best for: Long sales cycles, education-focused</div>
            </div>
          </div>
        )
      }
    }
  };

  const handleChipClick = (chipId: string) => {
    setActiveChipCategory(chipId);
    // Set default option for the selected category
    const defaultOption = previewData[chipId as keyof typeof previewData]?.options[0]?.id;
    if (defaultOption) {
      setActivePreviewOption(defaultOption);
    }
  };

  const handleOptionClick = (optionId: string) => {
    setActivePreviewOption(optionId);
  };

  const benefits = [
    'Turn agents on/off anytime',
    'Approve tone & messaging once',
    'Guardrails, quiet hours & DNC honored'
  ];

  const currentCategoryData = previewData[activeChipCategory as keyof typeof previewData];
  const currentContent = currentCategoryData?.content[activePreviewOption as keyof typeof currentCategoryData.content];

  return (
    <section className="relative py-12 px-6">
      <div className="w-full max-w-7xl mx-auto">
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start max-w-6xl mx-auto">
          
          {/* Left Column - Intro (40%) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Headline */}
            <div className="space-y-4">
              <h2 className="text-text-primary font-black text-4xl lg:text-5xl leading-tight tracking-tight">
                Your Team,
                Your Playbook
              </h2>
              <div className="w-24 h-1 bg-gradient-brand"></div>
            </div>

            {/* Description */}
            <p className="text-text-secondary text-lg leading-relaxed">
              Customize every aspect of your AI sales team to match your exact process, tone, and strategy.
            </p>

            {/* Benefits List */}
            <ul className="space-y-3">
              {benefits.map((benefit, index) => (
                <li 
                  key={index}
                  className="flex items-center space-x-3"
                >
                  <CheckCircle className="w-5 h-5 text-brand-green-teal flex-shrink-0" />
                  <span className="text-text-primary text-base">{benefit}</span>
                </li>
              ))}
            </ul>

            {/* CTA Button - Visible for debugging */}
            <div className="hidden lg:block">
              <button 
                onClick={onOpenDemoModal}
                className="px-6 py-3 rounded-xl bg-gradient-cta text-cta-text font-bold text-base transition-all duration-300 hover:shadow-xl hover:scale-105 cta-hover-overlay"
              >
                Build Your Custom AI Sales Team
              </button>
            </div>
          </div>

          {/* Right Column - Interactive Preview (60%) */}
          <div className="lg:col-span-3">
            <div className="relative bg-bg-surface border border-card-border rounded-2xl p-6 shadow-glow-combined card-inner-glow overflow-hidden">
              {/* Background glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/5 to-brand-green-teal/5 rounded-2xl" />
              
              <div className="relative z-10">
                {/* Top Row - Category Chips */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {chips.map((chip) => (
                    <button
                      key={chip.id}
                      onClick={() => handleChipClick(chip.id)}
                      className={`
                        px-0.5 py-0.5 text-[0.63rem] sm:px-3 sm:py-2 rounded-lg sm:text-sm font-medium transition-all duration-300 whitespace-nowrap
                        ${activeChipCategory === chip.id
                          ? 'bg-chip-bg border border-brand-blue text-brand-blue shadow-chip-glow'
                          : 'bg-chip-bg border border-chip-border text-chip-text hover:border-brand-blue/50'
                        }
                      `}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>

                {/* Option Selection Buttons */}
                {currentCategoryData && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {currentCategoryData.options.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleOptionClick(option.id)}
                        className={`
                          px-0.5 py-0.5 text-[0.63rem] sm:px-3 sm:py-2 rounded-lg sm:text-sm font-medium transition-all duration-300
                          ${activePreviewOption === option.id
                            ? 'bg-brand-blue/20 border border-brand-blue text-brand-blue'
                            : 'bg-bg-surface border border-card-border text-text-secondary hover:border-brand-blue/30 hover:text-text-primary'
                          }
                        `}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Dynamic Content Preview */}
                <div className="min-h-[300px]">
                  {currentContent || (
                    <div className="flex items-center justify-center h-64 text-text-secondary">
                      Select a customization option to see the preview
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile CTA Button */}
        <div className="mt-6 sm:mt-8 text-center sm:hidden">
          <button 
            onClick={onOpenDemoModal}
            className="px-6 py-3 sm:px-8 sm:py-4 rounded-xl sm:rounded-2xl bg-gradient-cta text-cta-text font-bold text-base sm:text-lg transition-all duration-300 hover:shadow-xl hover:scale-105 cta-hover-overlay"
          >
            Deploy Your Custom AI Sales Team
          </button>
        </div>
      </div>
    </section>
  );
};

export default CustomizationSection;